import React from 'react';
import { 
    Grid,
    Box, 
    Typography, 
    Card,
    CardContent,
    Stack
} from '@mui/material';
import { empTypeHelper } from '../../helpers/empTypeHelper';
import { formatDate } from '../../helpers/handleDate';

const BasicDetails = ({ employeeData }) => {
    return (
        <Box sx={{ p: 3 }}>
            <Grid container spacing={4}>
                {/* Column 1 */}
                <Grid item xs={12} md={3.5}>
                    <Box mb={3}>
                        <Typography variant="body1" sx={{ color: '#9E9E9E', fontSize: '14px' }}>
                            Employee ID
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#000001DE', fontSize: { md: '16px', lg: '18px' } }}>
                            {employeeData?.basic?.userid || 'N/A'}
                        </Typography>
                    </Box>

                    <Box mb={3}>
                        <Typography variant="body1" sx={{ color: '#9E9E9E', fontSize: '14px' }}>
                            Employee Type
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#000001DE', fontSize: { md: '16px', lg: '18px' } }}>
                            {employeeData?.basic?.emp_type ? empTypeHelper(employeeData.basic.emp_type) : 'N/A'}
                        </Typography>
                    </Box>

                    <Box mb={3}>
                        <Typography variant="body1" sx={{ color: '#9E9E9E', fontSize: '14px' }}>
                            Qural Experience
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#000001DE', fontSize: { md: '16px', lg: '18px' } }}>
                            {employeeData?.basic?.conserv_exp || 'N/A'}
                        </Typography>
                    </Box>

                    <Box>
                        <Typography variant="body1" sx={{ color: '#9E9E9E', fontSize: '14px' }}>
                            Course
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#000001DE', fontSize: { md: '16px', lg: '18px' } }}>
                            {employeeData?.basic?.course || 'N/A'}
                        </Typography>
                    </Box>
                </Grid>

                {/* Column 2 */}
                <Grid item xs={12} md={3.5}>
                    <Box mb={3}>
                        <Typography variant="body1" sx={{ color: '#9E9E9E', fontSize: '14px' }}>
                            Reporting To
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#000001DE', fontSize: { md: '16px', lg: '18px' } }}>
                            {employeeData?.basic?.reportto || 'N/A'}
                        </Typography>
                    </Box>

                    <Box mb={3}>
                        <Typography variant="body1" sx={{ color: '#9E9E9E', fontSize: '14px' }}>
                            Organization DOJ
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#000001DE', fontSize: { md: '16px', lg: '18px' } }}>
                            {employeeData?.basic?.org_doj ? formatDate(employeeData.basic.org_doj) : 'N/A'}
                        </Typography>
                    </Box>

                    <Box mb={3}>
                        <Typography variant="body1" sx={{ color: '#9E9E9E', fontSize: '14px' }}>
                            Employment Status
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#000001DE', fontSize: { md: '16px', lg: '18px' } }}>
                            {employeeData?.basic?.estatus || 'N/A'}
                        </Typography>
                    </Box>

                    <Box>
                        <Typography variant="body1" sx={{ color: '#9E9E9E', fontSize: '14px' }}>
                            Batch
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#000001DE', fontSize: { md: '16px', lg: '18px' } }}>
                            {employeeData?.basic?.batch || 'N/A'}
                        </Typography>
                    </Box>
                </Grid>

                {/* Column 3 - Addresses */}
                <Grid item xs={12} md={5}>
                    <Box
                        sx={{
                            bgcolor: '#F5F7FA', // Adjusted to match the light blue-gray background in the PNG
                            p: 2,
                            borderRadius: '12px', // Slightly larger border radius to match the PNG
                            width: '100%',
                            maxWidth: '600px',
                            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)' // Subtle shadow for depth
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography
                                variant="h4"
                                component="h1"
                                sx={{
                                    fontWeight: 600,
                                    color: '#4B277B', // Matches the purple color in the PNG
                                    fontSize: '1.5rem', // Adjusted to match the PNG size
                                    ml: 1
                                }}
                            >
                                Address
                            </Typography>
                        </Box>

                        <Box sx={{ width: '100%', height: '1px', bgcolor: '#E0E0E0', mb: 3 }} /> {/* Lighter divider color */}

                        <Stack spacing={2}> {/* Reduced spacing to match the compact layout in the PNG */}
                            {/* Residential Address */}
                            <Card
                                elevation={0}
                                sx={{
                                    borderRadius: '8px', // Slightly smaller radius for inner cards
                                    bgcolor: '#FFFFFF', // White background for the card
                                    boxShadow: 'none' // No shadow to match the flat design in the PNG
                                }}
                            >
                                <CardContent sx={{ p: 2, pb: 2 }}> {/* Reduced padding to match the PNG */}
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            color: '#888',
                                            mb: 0.5, // Reduced margin to match the PNG
                                            fontWeight: 400,
                                            fontSize: '0.9rem' // Smaller font size to match the PNG
                                        }}
                                    >
                                        Residential Address
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: '#000',
                                            fontWeight: 500,
                                            fontSize: '1rem', // Adjusted font size to match the PNG
                                            lineHeight: 1.4 // Tighter line height to match the PNG
                                        }}
                                    >
                                        {employeeData?.basic?.permanent_address || 'N/A'}
                                    </Typography>
                                </CardContent>
                            </Card>

                            {/* Permanent Address */}
                            <Card
                                elevation={0}
                                sx={{
                                    borderRadius: '8px',
                                    bgcolor: '#FFFFFF',
                                    boxShadow: 'none'
                                }}
                            >
                                <CardContent sx={{ p: 2, pb: 2 }}>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            color: '#888',
                                            mb: 0.5,
                                            fontWeight: 400,
                                            fontSize: '0.9rem'
                                        }}
                                    >
                                        Permanent Address
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: '#000',
                                            fontWeight: 500,
                                            fontSize: '1rem',
                                            lineHeight: 1.4
                                        }}
                                    >
                                        {employeeData?.basic?.residential_address || 'N/A'}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Stack>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default BasicDetails;